import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Settings as SettingsIcon, Save, Sun, Moon, Monitor, Clock, Link2, Copy, RefreshCw, Power, Users, Building2, ChevronRight, ShieldCheck, Search, FileDown } from "lucide-react";
import { applyTheme, getStoredTheme, setStoredTheme } from "@/lib/theme";
import BackgroundLoopsSection from "@/pages/BackgroundLoopsSection";
import PrintQueueSection from "@/components/PrintQueueSection";
import TwoFactorSection from "@/pages/TwoFactorSection";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYNC_PRESETS = [7, 14, 30, 60, 90, 120, 180];

const THEME_OPTIONS = [
  { key: "dark", label: "Întunecat", icon: Moon, desc: "Fundal negru — implicit" },
  { key: "light", label: "Luminos", icon: Sun, desc: "Fundal alb" },
  { key: "auto", label: "Automat", icon: Monitor, desc: "Urmează preferința sistemului" },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [settings, setSettings] = useState({ sync_days: 60, theme: "dark" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/settings/global`);
      const next = { sync_days: 60, theme: "dark", ...(r.data || {}) };
      // Local theme override (per-device)
      const stored = getStoredTheme();
      if (stored) next.theme = stored;
      setSettings(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea setărilor");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onThemeChange = (key) => {
    setSettings((s) => ({ ...s, theme: key }));
    setStoredTheme(key);
    applyTheme(key);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { sync_days: Number(settings.sync_days) || 60, theme: settings.theme || "dark" };
      await axios.put(`${API}/settings/global`, payload);
      toast.success("Setări salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvarea a eșuat");
    } finally {
      setSaving(false);
    }
  };

  const [activeTab, setActiveTab] = useState("general");
  const tabs = [
    { key: "general",      label: "General",              testid: "settings-tab-general" },
    { key: "automations",  label: "Automatizări",         testid: "settings-tab-automations" },
    { key: "orders",       label: "Comenzi & Facturi",    testid: "settings-tab-orders" },
    { key: "sameday",      label: "Sameday",              testid: "settings-tab-sameday" },
    { key: "integrations", label: "Integrări & Diagnostic", testid: "settings-tab-integrations" },
  ];

  if (loading) {
    return <div className="p-8 text-zinc-500" data-testid="settings-loading">Se încarcă...</div>;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6 md:p-8" data-testid="settings-page">
      <header className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/15 text-orange-300">
          <SettingsIcon className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">Setări globale</h1>
          <p className="text-xs text-zinc-500">Preferințe aplicate la nivel de aplicație</p>
        </div>
      </header>

      {/* Sub-tabs bar */}
      <div className="sticky top-0 z-10 -mx-6 flex flex-wrap gap-1 border-b border-zinc-800 bg-zinc-950/95 px-6 pb-1 backdrop-blur md:-mx-8 md:px-8" data-testid="settings-tabs">
        {tabs.map((t) => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setActiveTab(t.key)}
              className={`relative px-3 py-2 text-sm font-medium transition ${
                active ? "text-orange-300" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {t.label}
              {active && <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-orange-400" />}
            </button>
          );
        })}
      </div>

      {/* ═══════════════ GENERAL ═══════════════ */}
      {activeTab === "general" && <>

      {/* Acces rapid — Echipa (colaboratori) + Date companie (owner-only) */}
      {isOwner && (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="settings-quick-access">
          <Link
            to="/team"
            data-testid="settings-nav-team"
            className="group flex items-center justify-between rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 transition hover:border-orange-500/40 hover:bg-zinc-900/70"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/10 text-orange-300">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-100">Colaboratori (Echipa)</p>
                <p className="text-[11px] text-zinc-500">Utilizatori, invitații, permisiuni pe pagini</p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-zinc-500 transition group-hover:translate-x-0.5 group-hover:text-orange-300" />
          </Link>
          <Link
            to="/company"
            data-testid="settings-nav-company"
            className="group flex items-center justify-between rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 transition hover:border-orange-500/40 hover:bg-zinc-900/70"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/10 text-orange-300">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-100">Date companie</p>
                <p className="text-[11px] text-zinc-500">CUI, adresă, contact, IBAN — folosite pe facturi</p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-zinc-500 transition group-hover:translate-x-0.5 group-hover:text-orange-300" />
          </Link>
        </section>
      )}

      {/* Coada de printare AWB (mutată din top bar) */}
      <PrintQueueSection />

      {/* 2FA — owner only, hides itself for other roles */}
      {isOwner && <TwoFactorSection />}

      {/* Emite garanție manuală (offline / non-eMAG orders) */}
      <ManualWarrantySection />

      {/* Sync window */}
      <section className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6">
        <div className="mb-4 flex items-center gap-2">
          <Clock className="h-4 w-4 text-zinc-400" />
          <h2 className="text-base font-semibold text-zinc-100">Sincronizare comenzi</h2>
        </div>
        <p className="mb-4 text-xs text-zinc-500">
          Fereastra de timp folosită la sincronizările automate și manuale pentru eMAG / Trendyol / Gomag.
          Comenzile mai vechi nu vor fi descărcate.
        </p>

        <label className="mb-2 block text-[11px] uppercase tracking-wider text-zinc-500">
          Sincronizează comenzile din ultimele <span className="text-orange-300">{settings.sync_days}</span> zile
        </label>
        <input
          data-testid="settings-sync-days-input"
          type="number"
          min={1}
          max={180}
          value={settings.sync_days}
          onChange={(e) => setSettings((s) => ({ ...s, sync_days: e.target.value }))}
          className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {SYNC_PRESETS.map((d) => (
            <button
              key={d}
              data-testid={`settings-sync-preset-${d}`}
              onClick={() => setSettings((s) => ({ ...s, sync_days: d }))}
              className={`rounded-full border px-3 py-1 text-[11px] font-medium transition ${
                Number(settings.sync_days) === d
                  ? "border-orange-500/50 bg-orange-500/15 text-orange-200"
                  : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
              }`}
            >
              {d} zile
            </button>
          ))}
        </div>
      </section>

      {/* Theme */}
      <section className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6">
        <div className="mb-4 flex items-center gap-2">
          <Sun className="h-4 w-4 text-zinc-400" />
          <h2 className="text-base font-semibold text-zinc-100">Temă</h2>
        </div>
        <p className="mb-4 text-xs text-zinc-500">Aplicată imediat la nivel de browser, salvată local per dispozitiv.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {THEME_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const active = settings.theme === opt.key;
            return (
              <button
                key={opt.key}
                data-testid={`settings-theme-${opt.key}`}
                onClick={() => onThemeChange(opt.key)}
                className={`flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition ${
                  active
                    ? "border-orange-500/50 bg-orange-500/10"
                    : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
                }`}
              >
                <Icon className={`h-5 w-5 ${active ? "text-orange-300" : "text-zinc-400"}`} />
                <span className={`text-sm font-semibold ${active ? "text-orange-200" : "text-zinc-100"}`}>{opt.label}</span>
                <span className="text-[11px] text-zinc-500">{opt.desc}</span>
              </button>
            );
          })}
        </div>
      </section>

      <div className="flex justify-end">
        <button
          data-testid="settings-save-btn"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-rose-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 hover:from-orange-400 hover:to-rose-400 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {saving ? "Se salvează..." : "Salvează"}
        </button>
      </div>

      </>}
      {/* ═══════════════ AUTOMATIZĂRI ═══════════════ */}
      {activeTab === "automations" && <>

      {/* Procese automate (background loops) */}
      <BackgroundLoopsSection />

      {/* Raport prețuri bi-săptămânal */}
      <PriceReportSection />

      {/* Rapoarte lunare (1 a lunii, 09:00 RO → bragusidea01@gmail.com) */}
      <MonthlyReportsSection />

      </>}
      {/* ═══════════════ COMENZI & FACTURI ═══════════════ */}
      {activeTab === "orders" && <>

      {/* Backfill istoric */}
      <BackfillSection syncDays={settings.sync_days} />

      {/* Link public Comandă manuală */}
      <PublicManualOrderSection />

      {/* Taxe livrare pentru comandă manuală (home / locker) */}
      <ManualShippingSection />

      {/* Emite garanție manuală (offline / non-eMAG orders) */}
      <ManualWarrantySection />

      {/* Semnătură PV Service */}
      <PvSignatureSection />

      {/* Mapare SKU-uri (legacy → canonical) */}
      <SkuAliasesSection />

      </>}
      {/* ═══════════════ SAMEDAY ═══════════════ */}
      {activeTab === "sameday" && <>

      {/* Kg extra pe SKU pentru calcul Sameday (v2 formula) */}
      <SamedayProductKgExtraSection />

      </>}
      {/* ═══════════════ INTEGRĂRI ═══════════════ */}
      {activeTab === "integrations" && <>

      {/* Marketplace Offers Diagnostics — mutat aici din pagina Oferte
          Marketplace (Feb 2026): actiuni rare de mentenanta / dev-ops. */}
      <MarketplaceDiagnosticsSection />

      {/* WhatsApp Business Cloud API */}
      <WhatsAppConfigSection />

      </>}
    </div>
  );
}

function WhatsAppConfigSection() {
  const [cfg, setCfg] = useState({});
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/whatsapp/config`);
      setCfg(r.data || {});
      setDraft({});
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      // Only send fields the operator actually changed — backend preserves the
      // existing secret/token when omitted.
      await axios.post(`${API}/whatsapp/config`, draft);
      toast.success("Configurare WhatsApp salvată");
      setDraft({});
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : err.message);
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/whatsapp/test-connection`);
      setTestResult({ ok: true, ...r.data });
      toast.success(`Conexiune OK · ${r.data?.verified_name || r.data?.display_phone_number}`);
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setTestResult({ ok: false, error: typeof d === "string" ? d : err.message });
      toast.error("Conexiune eșuată");
    } finally {
      setTesting(false);
    }
  };

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const v = (k) => (draft[k] !== undefined ? draft[k] : (cfg[k] || ""));

  const isConfigured = !!cfg.phone_number_id;

  return (
    <section className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="whatsapp-config-section">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-300">
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M20.5 3.5A11 11 0 0 0 3.6 17.1L2 22l5.1-1.5A11 11 0 1 0 20.5 3.5Zm-8.5 17a9 9 0 0 1-4.6-1.3l-.3-.2-3 .9.9-3-.2-.3a9 9 0 1 1 7.2 4Zm5.2-6.7c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1l-1 1.2c-.2.2-.4.2-.7.1a7.4 7.4 0 0 1-3.6-3.2c-.3-.5.3-.5.8-1.5.1-.2 0-.4 0-.5l-.9-2.1c-.2-.5-.4-.4-.6-.4h-.6c-.2 0-.5.1-.8.4-.3.3-1.1 1-1.1 2.5s1.1 2.9 1.3 3.1c.2.3 2.3 3.5 5.6 4.9.8.3 1.4.5 1.9.7.8.3 1.5.2 2.1.1.6-.1 1.7-.7 2-1.3.2-.6.2-1.2.2-1.3-.1-.1-.3-.2-.6-.3Z"/></svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-zinc-100">WhatsApp Business</h2>
            <p className="text-xs text-zinc-500">Meta Cloud API · Webhook + trimitere</p>
          </div>
        </div>
        {isConfigured && (
          <span className="rounded-md bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
            Configurat
          </span>
        )}
      </div>

      <div className="space-y-3">
        <Field label="Phone Number ID" hint="ID-ul numărului din Meta Business — WhatsApp → API Setup">
          <input
            type="text" value={v("phone_number_id")} onChange={set("phone_number_id")}
            placeholder="ex. 123456789012345"
            data-testid="wa-cfg-phone-id"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500"
          />
        </Field>

        <Field label="WhatsApp Business Account ID (WABA ID)">
          <input
            type="text" value={v("waba_id")} onChange={set("waba_id")}
            placeholder="ex. 987654321098765"
            data-testid="wa-cfg-waba-id"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500"
          />
        </Field>

        <Field label="Permanent Access Token" hint="System User token cu permisiunile whatsapp_business_messaging + whatsapp_business_management">
          <input
            type="password" value={draft.access_token ?? ""} onChange={set("access_token")}
            placeholder={cfg.access_token_masked || "EAAGmgQ..."}
            data-testid="wa-cfg-token"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-xs text-zinc-100 outline-none focus:border-emerald-500"
          />
        </Field>

        <Field label="Verify Token (Webhook)" hint="Șir arbitrar — îl pui IDENTIC în Meta la configurarea webhook-ului">
          <input
            type="text" value={v("verify_token")} onChange={set("verify_token")}
            placeholder="ex. bragus_wa_2026"
            data-testid="wa-cfg-verify-token"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-xs text-zinc-100 outline-none focus:border-emerald-500"
          />
        </Field>

        <Field label="App Secret" hint="Meta → App Settings → Basic. Folosit pentru validarea semnăturii X-Hub-Signature-256.">
          <input
            type="password" value={draft.app_secret ?? ""} onChange={set("app_secret")}
            placeholder={cfg.app_secret_masked || ""}
            data-testid="wa-cfg-app-secret"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-xs text-zinc-100 outline-none focus:border-emerald-500"
          />
        </Field>

        <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">URL Webhook (pune-l în Meta)</div>
          <code className="block break-all font-mono text-xs text-emerald-300" data-testid="wa-webhook-url">
            {process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook
          </code>
          <p className="mt-1.5 text-[10px] text-zinc-500">
            Meta → WhatsApp → Configuration → Webhook · Subscribe la <code className="font-mono text-zinc-400">messages</code> + <code className="font-mono text-zinc-400">message_status</code>
          </p>
        </div>

        {cfg.display_phone_number && (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs">
            <div className="font-semibold text-emerald-300">Număr activ: {cfg.display_phone_number}</div>
            <div className="text-zinc-400">Verified name: {cfg.verified_name || "—"} · Quality: {cfg.quality_rating || "—"}</div>
          </div>
        )}

        {testResult && (
          <div className={`rounded-md border p-2 text-xs ${testResult.ok ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" : "border-rose-500/30 bg-rose-500/5 text-rose-300"}`}>
            {testResult.ok ? `OK · ${testResult.verified_name || testResult.display_phone_number}` : `Eroare: ${testResult.error}`}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button" onClick={save} disabled={busy || Object.keys(draft).length === 0}
            data-testid="wa-cfg-save"
            className="rounded-md bg-orange-500 px-4 py-2 text-xs font-semibold text-zinc-950 hover:bg-orange-400 disabled:opacity-50"
          >
            {busy ? "Salvez..." : "Salvează"}
          </button>
          <button
            type="button" onClick={test} disabled={testing || !isConfigured}
            data-testid="wa-cfg-test"
            className="rounded-md border border-zinc-700 px-4 py-2 text-xs font-medium text-zinc-200 hover:bg-zinc-900 disabled:opacity-50"
          >
            {testing ? "Testez..." : "Testează conexiunea"}
          </button>
          <span className="text-[10px] text-zinc-500">
            Credențialele se obțin din Meta Business Manager → WhatsApp.
          </span>
        </div>
      </div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[10px] text-zinc-600">{hint}</p>}
    </div>
  );
}

function PriceReportSection() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rates, setRates] = useState(null);

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/price-report/config`),
        axios.get(`${API}/price-report/exchange-rates`),
      ]);
      setCfg(c.data); setRates(r.data);
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async (patch) => {
    try {
      const r = await axios.put(`${API}/price-report/config`, patch);
      setCfg(r.data); toast.success("Setări salvate");
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/price-report/generate`, {}, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `preturi_bragus_${new Date().toISOString().slice(0,10).replace(/-/g, "")}.xlsx`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success(`Descărcat (${r.headers["x-row-count"]} rânduri)`);
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    if (!window.confirm(`Trimit raportul către ${cfg?.recipient}?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/price-report/send`, {});
      toast.success(`Trimis (${r.data.rows} rânduri) către ${r.data.recipient}`);
      load();
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-5" data-testid="price-report-section">
      <h2 className="mb-3 text-sm font-semibold text-zinc-100">📊 Raport prețuri bi-săptămânal</h2>
      <p className="mb-4 text-[11px] text-zinc-500">
        Tabel cu prețurile (cu TVA) din toate canalele active: eMAG BAU+FBE pe RO/BG/HU (convertite în RON la cursul BNR), Trendyol, BRAGUS.ro.
        Trimitere automată în fiecare a doua zi de luni dimineață.
      </p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Email destinație</label>
          <input value={cfg.recipient || ""} onChange={(e) => setCfg({ ...cfg, recipient: e.target.value })}
            onBlur={(e) => saveCfg({ recipient: e.target.value })}
            data-testid="price-report-recipient"
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
        </div>
        <div className="flex items-end">
          <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-zinc-300">
            <input type="checkbox" checked={cfg.enabled !== false}
              onChange={(e) => saveCfg({ enabled: e.target.checked })}
              data-testid="price-report-enabled"
              className="h-4 w-4 rounded accent-orange-500" />
            Trimite automat bi-săptămânal (luni dimineață)
          </label>
        </div>
      </div>

      {rates?.rates && (
        <div className="mb-4 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-[11px] text-zinc-400">
          Curs BNR: {["EUR", "BGN", "HUF", "TRY"].filter(c => rates.rates[c]).map(c => (
            <span key={c} className="mr-3">1 {c} = <span className="font-mono text-zinc-200">{rates.rates[c]?.toFixed(4)}</span> RON</span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-[11px] text-zinc-500">
          Ultimul trimis: {cfg.last_sent_at ? new Date(cfg.last_sent_at).toLocaleString("ro-RO") : "niciodată"}
          {cfg.last_rows ? ` · ${cfg.last_rows} rânduri` : ""}
        </p>
        <div className="flex gap-2">
          <button onClick={download} disabled={busy} data-testid="price-report-download"
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-200 disabled:opacity-50">
            ⬇ Descarcă acum
          </button>
          <button onClick={sendNow} disabled={busy} data-testid="price-report-send"
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
            ✉ Trimite acum
          </button>
        </div>
      </div>
    </div>
  );
}

function MonthlyReportsSection() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/reports/monthly/status`);
      setStatus(r.data);
    } catch (e) { toast.error("Eroare status: " + (e.response?.data?.detail || e.message)); }
  };
  useEffect(() => { load(); }, []);

  const sendNow = async (kind, label) => {
    if (!window.confirm(`Trimit ACUM raportul „${label}" către ${status?.recipient}?`)) return;
    setBusy(kind);
    try {
      const url = kind === "returns"
        ? `${API}/reports/monthly/returns/send-now?half=current`
        : kind === "transport"
          ? `${API}/financiar/transport-report/send-now?half=current`
          : `${API}/reports/monthly/${kind}/send-now`;
      const r = await axios.post(url);
      toast.success(`Trimis: ${r.data.month_label || "OK"}`);
      load();
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(""); }
  };

  // Force-resend for a specific past month (user request 2026-09-01).
  const sendForMonth = async (kind, monthLabel) => {
    const target = window.prompt(
      `Retrimit raportul „${monthLabel}" pentru o lună specifică (YYYY-MM, ex. 2026-08).\n` +
      `Șterge claim-ul existent și regenerează emailul.`,
      new Date(Date.now() - 30 * 24 * 3600_000).toISOString().slice(0, 7)
    );
    if (!target || !/^\d{4}-\d{2}$/.test(target)) return;
    if (!window.confirm(`Retrimit raportul „${monthLabel}" pentru ${target} către ${status?.recipient}?`)) return;
    setBusy(`${kind}-resend`);
    try {
      const url = kind === "returns"
        ? `${API}/reports/monthly/returns/send-now?force=true&month=${target}`
        : `${API}/reports/monthly/${kind}/send-now?force=true&month=${target}`;
      const r = await axios.post(url);
      toast.success(`Trimis pentru ${r.data.month_label || target}`);
      load();
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(""); }
  };

  if (!status) return null;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("ro-RO") : "niciodată";

  const cards = [
    {
      kind: "returns",
      label: "Retururi & Garanții (bilunar)",
      desc: "Data 1 + Data 16. Include neridicate + storno Gomag. Test = perioada curentă → acum.",
      icon: "📦",
      color: "emerald",
      hist: status.returns,
      extra: (h) => h && `${h.grand_count || 0} cereri · ${(h.grand_total || 0).toFixed(2)} RON`,
    },
    {
      kind: "transport",
      label: "Transport Sameday (bilunar)",
      desc: "Venit taxe transport vs cost Sameday. Data 1 + Data 16 pentru facturi bilunare.",
      icon: "🚚",
      color: "cyan",
      hist: status.transport,
      extra: (h) => h && `Net: ${(h.net_profit || 0) >= 0 ? "+" : ""}${(h.net_profit || 0).toFixed(2)} RON`,
    },
    {
      kind: "compensations",
      label: "Despăgubiri eMAG (NET)",
      desc: "Baseline + cereri finalizate − cost rebuturi Oblio = diferență NET.",
      icon: "💰",
      color: "sky",
      hist: status.compensations,
      extra: (h) => h && `NET: ${(h.net || 0) >= 0 ? "+" : ""}${(h.net || 0).toFixed(2)} RON`,
    },
  ];

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-5" data-testid="monthly-reports-section">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-zinc-100">📅 Rapoarte lunare automate</h2>
          <p className="mt-1 text-[11px] text-zinc-500">
            {status.schedule}. Destinatar: <span className="font-mono text-zinc-300">{status.recipient}</span>
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {cards.map((c) => {
          const isBusy = busy === c.kind;
          const borderCls = {
            emerald: "border-emerald-500/30",
            sky: "border-sky-500/30",
            rose: "border-rose-500/30",
            cyan: "border-cyan-500/30",
          }[c.color];
          const btnCls = {
            emerald: "bg-emerald-500/10 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/20",
            sky: "bg-sky-500/10 border-sky-500/40 text-sky-200 hover:bg-sky-500/20",
            rose: "bg-rose-500/10 border-rose-500/40 text-rose-200 hover:bg-rose-500/20",
            cyan: "bg-cyan-500/10 border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/20",
          }[c.color];
          return (
            <div key={c.kind} data-testid={`monthly-report-card-${c.kind}`}
              className={`rounded-lg border ${borderCls} bg-zinc-900/40 p-3`}>
              <div className="mb-1 text-lg">{c.icon}</div>
              <div className="text-xs font-semibold text-zinc-100">{c.label}</div>
              <p className="mt-1 text-[10px] leading-relaxed text-zinc-500">{c.desc}</p>
              <div className="mt-3 rounded border border-zinc-800 bg-zinc-950/60 p-2 text-[10px] text-zinc-400">
                <div>Ultimul trimis: <span className="text-zinc-300">{fmtDate(c.hist?.sent_at)}</span></div>
                {c.hist?.month_label && (
                  <div className="mt-0.5">Luna: <span className="text-zinc-300">{c.hist.month_label}</span></div>
                )}
                {c.extra(c.hist) && (
                  <div className="mt-0.5 font-mono">{c.extra(c.hist)}</div>
                )}
              </div>
              <button onClick={() => sendNow(c.kind, c.label)} disabled={!!busy}
                data-testid={`monthly-report-send-${c.kind}`}
                className={`mt-3 w-full rounded-md border px-3 py-1.5 text-xs font-medium transition disabled:opacity-50 ${btnCls}`}>
                {isBusy ? "Se trimite..." : "✉ Trimite acum"}
              </button>
              {(c.kind === "returns" || c.kind === "compensations") && (
                <button onClick={() => sendForMonth(c.kind, c.label)} disabled={!!busy}
                  data-testid={`monthly-report-resend-${c.kind}`}
                  className="mt-1.5 w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-300 hover:text-zinc-100 disabled:opacity-50">
                  {busy === `${c.kind}-resend` ? "Se retrimite..." : "↻ Retrimite pentru lună anume"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SkuAliasesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ alias: "", canonical: "", note: "" });

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sku-aliases`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const add = async () => {
    const alias = (draft.alias || "").trim().toUpperCase();
    const canonical = (draft.canonical || "").trim();
    if (!alias || !canonical) {
      toast.error("Completează ambele câmpuri");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/sku-aliases`, { alias, canonical, note: draft.note });
      toast.success(`${alias} → ${canonical}`);
      setDraft({ alias: "", canonical: "", note: "" });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (alias) => {
    if (!window.confirm(`Ștergi maparea pentru ${alias}?`)) return;
    try {
      await axios.delete(`${API}/sku-aliases/${encodeURIComponent(alias)}`);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <section
      data-testid="settings-sku-aliases-section"
      className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5"
    >
      <div className="mb-2 flex items-center gap-2">
        <RefreshCw className="h-4 w-4 text-orange-400" />
        <h2 className="text-base font-semibold text-zinc-100">Mapare SKU-uri (legacy → canonical)</h2>
      </div>
      <p className="mb-4 text-xs text-zinc-500">
        Rescrie codurile eMAG greșite cu SKU-urile reale din Oblio. Se aplică la emiterea facturilor
        (descărcare gestiune) și la afișarea imaginilor de produs pe pagina Comenzi.
      </p>

      {/* Add form */}
      <div className="mb-4 grid grid-cols-12 gap-2">
        <input
          data-testid="sku-alias-add-alias"
          value={draft.alias}
          onChange={(e) => setDraft({ ...draft, alias: e.target.value.toUpperCase() })}
          placeholder="SKU greșit (ex. BRAG-7)"
          className="col-span-3 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs font-mono text-zinc-100 outline-none focus:border-orange-500/50"
        />
        <div className="col-span-1 flex items-center justify-center text-zinc-500">→</div>
        <input
          data-testid="sku-alias-add-canonical"
          value={draft.canonical}
          onChange={(e) => setDraft({ ...draft, canonical: e.target.value })}
          placeholder="SKU corect (ex. BRAG-193)"
          className="col-span-3 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs font-mono text-zinc-100 outline-none focus:border-orange-500/50"
        />
        <input
          data-testid="sku-alias-add-note"
          value={draft.note}
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          placeholder="Notă opțională"
          className="col-span-3 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500/50"
        />
        <button
          data-testid="sku-alias-add-btn"
          onClick={add}
          disabled={busy}
          className="col-span-2 rounded-md bg-emerald-500 px-3 text-xs font-semibold text-zinc-950 hover:bg-emerald-400 disabled:opacity-50"
        >
          {busy ? "..." : "Adaugă mapare"}
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500">Se încarcă...</div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-900/40 px-3 py-4 text-center text-xs text-zinc-500">
          Nicio mapare configurată
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-zinc-800">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">SKU greșit</th>
                <th className="w-8"></th>
                <th className="px-3 py-2 text-left">SKU corect</th>
                <th className="px-3 py-2 text-left">Notă</th>
                <th className="px-3 py-2 text-right">Acțiuni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {items.map((it) => (
                <tr key={it.alias} data-testid={`sku-alias-row-${it.alias}`}>
                  <td className="px-3 py-2 font-mono text-rose-300">{it.alias}</td>
                  <td className="text-center text-zinc-600">→</td>
                  <td className="px-3 py-2 font-mono text-emerald-300">{it.canonical}</td>
                  <td className="px-3 py-2 text-zinc-400">{it.note || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      data-testid={`sku-alias-delete-${it.alias}`}
                      onClick={() => remove(it.alias)}
                      className="rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[10px] font-medium text-rose-300 hover:bg-rose-500/20"
                    >
                      Șterge
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PvSignatureSection() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/returns/settings/pv-signature`);
      setInfo(r.data);
      // The preview endpoint requires JWT auth so we can't drop it straight
      // into an <img src=…> attribute; fetch as blob and turn it into a
      // local object URL instead. Revoked on next refresh.
      if (r.data?.exists) {
        try {
          const blob = await axios.get(`${API}/returns/settings/pv-signature/preview`, { responseType: "blob" });
          const url = URL.createObjectURL(blob.data);
          setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
        } catch { /* ignore preview-only errors */ }
      } else {
        setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Fișierul trebuie să fie ≤ 2 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/returns/settings/pv-signature`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Semnătură încărcată");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Ștergi semnătura încărcată? PV-urile vor folosi numele tipărit cu font cursiv.")) return;
    try {
      await axios.delete(`${API}/returns/settings/pv-signature`);
      toast.success("Semnătură ștearsă");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <section
      data-testid="settings-pv-signature-section"
      className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5"
    >
      <div className="mb-2 flex items-center gap-2">
        <Save className="h-4 w-4 text-emerald-400" />
        <h2 className="text-base font-semibold text-zinc-100">Semnătură PV Service</h2>
      </div>
      <p className="mb-4 text-xs text-zinc-500">
        Imaginea se aplică automat pe colțul dreapta-jos al fiecărui PV Service generat.
        PNG cu fundal transparent este recomandat. Maximum 2 MB.
      </p>

      {loading ? (
        <div className="text-xs text-zinc-500">Se încarcă...</div>
      ) : (
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex h-32 w-64 items-center justify-center rounded-xl border border-dashed border-zinc-800 bg-zinc-900/60">
            {preview ? (
              <img
                data-testid="pv-signature-preview"
                src={preview}
                alt="Semnătură"
                className="max-h-28 max-w-60 object-contain"
              />
            ) : (
              <span className="text-[11px] text-zinc-600">Nicio semnătură încărcată</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label
              data-testid="pv-signature-upload"
              className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-emerald-400"
            >
              {uploading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {info?.exists ? "Înlocuiește semnătura" : "Încarcă semnătura (PNG/JPEG)"}
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onPick} disabled={uploading} />
            </label>
            {info?.exists && (
              <>
                <div className="text-[11px] text-zinc-500">
                  <span className="text-zinc-300">{info.filename}</span>
                  <span className="ml-2">{Math.round((info.size || 0) / 1024)} KB · {info.content_type}</span>
                </div>
                <button
                  data-testid="pv-signature-delete"
                  onClick={onDelete}
                  className="inline-flex w-fit items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-[11px] text-rose-300 hover:bg-rose-500/20"
                >
                  Șterge semnătura
                </button>
              </>
            )}
            {!info?.exists && (
              <p className="text-[11px] text-zinc-500">
                Fallback: dacă nu încarci o imagine, PV-ul va folosi numele „Dziuzenschi Cazimir&rdquo;
                scris cu font cursiv.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function BackfillSection({ syncDays }) {
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const runSyncNow = async () => {
    const days = Number(syncDays) || 30;
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await axios.post(`${API}/orders/sync`, { days });
      const fetched = r.data?.fetched ?? 0;
      setSyncResult({ ok: true, fetched, days });
      toast.success(`Sincronizare reușită: ${fetched} comenzi în ultimele ${days} zile.`);
    } catch (e) {
      setSyncResult({ ok: false, error: e?.response?.data?.detail || e.message });
      toast.error(`Sync eșuat: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const run = async (days, label) => {
    if (!window.confirm(`Pornesc backfill ${label} pe TOATE conturile (eMAG RO/BG/HU FBE+BAU + Gomag + Trendyol)?\n\nRulează în background ~5-30 min. Comenzile noi apar treptat pe măsură ce sunt importate.`)) return;
    setRunning(true);
    setLastResult(null);
    try {
      const r = await axios.post(`${API}/orders/backfill-all?days=${days}`);
      setLastResult({ ok: true, label, integrations: r.data?.integrations?.length || 0, note: r.data?.note });
      toast.success(`Backfill ${label} pornit pe ${r.data?.integrations?.length || 0} conturi`);
    } catch (e) {
      setLastResult({ ok: false, error: e?.response?.data?.detail || e.message });
      toast.error(`Backfill eșuat: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const PRESETS = [
    { days: 7,   label: "7 zile",  desc: "Pentru recuperare rapidă (ultima săptămână)" },
    { days: 30,  label: "30 zile", desc: "Luna trecută" },
    { days: 180, label: "6 luni",  desc: "Jumătate de an — backfill mediu" },
    { days: 365, label: "1 an",    desc: "An întreg — rulează o singură dată pe mediu nou" },
  ];

  return (
    <section
      data-testid="settings-backfill-section"
      className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5"
    >
      <div className="mb-4 flex items-center gap-2">
        <Clock className="h-4 w-4 text-amber-400" />
        <h2 className="text-base font-semibold text-zinc-100">Backfill istoric comenzi</h2>
      </div>
      <p className="mb-4 text-xs text-zinc-500">
        Re-importă comenzi vechi din toate platformele active. Util după un redeploy
        sau când vrei să recuperezi un interval pe care l-ai pierdut. Rulează în
        background, vezi rezultatele în pagina Comenzi.
      </p>

      {/* Sync acum — mutat din pagina Comenzi (Feb 2026). Foloseste fereastra
          "Sincronizare comenzi" configurata mai sus in setari (default 30 zile). */}
      <div className="mb-4 rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-zinc-100">Sincronizare rapidă acum</p>
            <p className="mt-0.5 text-[11px] text-zinc-500">
              Rulează sync manual pe fereastra din setări (<span className="text-orange-300">{syncDays || 30} zile</span>) —
              refresh instant pe toate platformele active.
            </p>
          </div>
          <button
            data-testid="settings-sync-now-btn"
            onClick={runSyncNow}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded-lg border border-orange-500/40 bg-orange-500/10 px-4 py-2 text-sm font-medium text-orange-200 transition hover:border-orange-500/70 hover:bg-orange-500/20 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizare..." : "Sincronizează acum"}
          </button>
        </div>
        {syncResult && syncResult.ok && (
          <p className="mt-2 text-[11px] text-emerald-300">
            ✓ {syncResult.fetched} comenzi în ultimele {syncResult.days} zile.
          </p>
        )}
        {syncResult && !syncResult.ok && (
          <p className="mt-2 text-[11px] text-rose-300">✕ {syncResult.error}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {PRESETS.map((p) => (
          <button
            key={p.days}
            data-testid={`backfill-${p.days}`}
            onClick={() => run(p.days, p.label)}
            disabled={running}
            className="flex flex-col items-start gap-1 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-left transition hover:border-amber-500/60 hover:bg-amber-500/10 disabled:opacity-50"
          >
            <span className="text-sm font-semibold text-amber-200">{p.label}</span>
            <span className="text-[11px] text-zinc-500">{p.desc}</span>
          </button>
        ))}
      </div>
      {running && (
        <p className="mt-4 text-xs text-amber-300">⏳ Se pornește backfill-ul în background...</p>
      )}
      {lastResult && lastResult.ok && (
        <p className="mt-4 text-xs text-emerald-300">
          ✓ Backfill {lastResult.label} pornit pe {lastResult.integrations} conturi.
          {lastResult.note && ` ${lastResult.note}`}
        </p>
      )}
      {lastResult && !lastResult.ok && (
        <p className="mt-4 text-xs text-rose-300">✕ {lastResult.error}</p>
      )}
    </section>
  );
}

function ManualShippingSection() {
  const [cfg, setCfg] = useState({ home: 19.99, locker: 15.99, vat_pct: 21 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/manual-order-shipping`);
        setCfg({
          home: Number(r.data.home),
          locker: Number(r.data.locker),
          vat_pct: Number(r.data.vat_pct),
        });
      } catch (e) { /* keep defaults */ }
    })();
  }, []);

  const save = async () => {
    const home = Number(cfg.home);
    const locker = Number(cfg.locker);
    const vat_pct = Number(cfg.vat_pct);
    if (!Number.isFinite(home) || home < 0) { toast.error("Home invalid"); return; }
    if (!Number.isFinite(locker) || locker < 0) { toast.error("Locker invalid"); return; }
    if (!Number.isFinite(vat_pct) || vat_pct < 0) { toast.error("TVA invalid"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/settings/manual-order-shipping`, { home, locker, vat_pct });
      toast.success("Taxe livrare salvate");
    } catch (e) {
      toast.error("Eroare: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-5" data-testid="manual-shipping-section">
      <h3 className="text-sm font-semibold text-zinc-100">Taxe livrare — Comandă manuală</h3>
      <p className="mt-1 text-[11px] text-zinc-500">
        Valorile default folosite când introduci o comandă manuală. Se aplică automat în funcție de tipul livrării (domiciliu / locker). Poți suprascrie punctual dintr-o comandă.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs text-zinc-400">Domiciliu (RON)</label>
          <input type="number" step="0.01" value={cfg.home}
            onChange={(e) => setCfg({ ...cfg, home: e.target.value })}
            data-testid="manual-shipping-home"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-sky-500/50 focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-zinc-400">Locker EasyBox (RON)</label>
          <input type="number" step="0.01" value={cfg.locker}
            onChange={(e) => setCfg({ ...cfg, locker: e.target.value })}
            data-testid="manual-shipping-locker"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-sky-500/50 focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-zinc-400">TVA (%)</label>
          <input type="number" step="0.01" value={cfg.vat_pct}
            onChange={(e) => setCfg({ ...cfg, vat_pct: e.target.value })}
            data-testid="manual-shipping-vat"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-sky-500/50 focus:outline-none" />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="manual-shipping-save"
          className="rounded-md bg-sky-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50">
          {saving ? "Se salvează..." : "Salvează"}
        </button>
      </div>
    </div>
  );
}

function MarketplaceDiagnosticsSection() {
  const [loading, setLoading] = useState(null); // "diag" | "heal" | "migrate" | null
  const [diagResult, setDiagResult] = useState(null);

  const runDiag = async () => {
    setLoading("diag"); setDiagResult(null);
    try {
      const r = await axios.get(`${API}/marketplace-offers/diagnostics`);
      setDiagResult(r.data?.accounts || []);
      toast.success(`Diagnostic gata — ${(r.data?.accounts || []).length} conturi verificate.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostic eșuat");
    } finally { setLoading(null); }
  };

  const runHeal = async () => {
    setLoading("heal");
    try {
      const preview = await axios.post(`${API}/marketplace-offers/heal-corrupt-prices`, {});
      const found = preview.data.found_count || 0;
      if (found === 0) {
        toast.info("Nu s-au găsit prețuri corupte. DB e curat!");
        return;
      }
      const items = preview.data.items || [];
      const sample = items.slice(0, 5).map(x => `${x.integration_key}/${x.sku}: ${x.sale_price_old_gross} → ${x.sale_price_new_gross} ${x.currency}`).join("\n");
      const ok = window.confirm(
        `S-au detectat ${found} prețuri corupte.\n\nPrimele:\n${sample}${items.length > 5 ? "\n..." : ""}\n\nRepar acum?`
      );
      if (!ok) return;
      const r = await axios.post(`${API}/marketplace-offers/heal-corrupt-prices`, { apply: true });
      toast.success(`${r.data.applied} prețuri reparate.`);
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(null); }
  };

  const runMigrate = async () => {
    if (!window.confirm("Migrează indexurile colecției products?\n\nDrop unique single-field _emag_id index legacy + recreate composite. Idempotent.")) return;
    setLoading("migrate");
    try {
      const r = await axios.post(`${API}/marketplace-offers/migrate-indexes`);
      const dropped = r.data?.dropped || [];
      if (dropped.length) toast.success(`Migrare reușită · sterse: ${dropped.join(", ")}`);
      else toast.info("Niciun index legacy de migrat — totul OK.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migrare eșuată");
    } finally { setLoading(null); }
  };

  return (
    <div className="mb-6 rounded-2xl border border-zinc-800 bg-zinc-950 p-6" data-testid="settings-marketplace-diag-section">
      <h2 className="mb-1 text-base font-semibold text-zinc-100">Diagnostics Oferte Marketplace</h2>
      <p className="mb-4 text-xs text-zinc-500">
        Acțiuni de mentenanță rulate rar (dev-ops). Le găsești aici după ce au fost mutate din pagina Oferte Marketplace.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
          <p className="text-sm font-semibold text-zinc-100">Diagnostic conturi</p>
          <p className="mt-0.5 text-[11px] text-zinc-500">Testează autentificarea pe toate conturile eMAG + Trendyol.</p>
          <button data-testid="settings-diag-btn" onClick={runDiag} disabled={loading === "diag"}
            className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-50">
            {loading === "diag" ? "Verific..." : "Rulează diagnostic"}
          </button>
          {diagResult && (
            <div className="mt-2 max-h-40 overflow-y-auto rounded border border-zinc-800 bg-zinc-950 p-2 text-[10px]">
              {diagResult.map((a, i) => (
                <div key={i} className="flex items-center justify-between border-b border-zinc-900 py-0.5 last:border-b-0">
                  <span className="truncate text-zinc-300">{a.name || a.integration_key}</span>
                  <span className={a.ok ? "text-emerald-300" : "text-rose-300"}>{a.ok ? "OK" : "FAIL"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
          <p className="text-sm font-semibold text-zinc-100">Repară prețuri</p>
          <p className="mt-0.5 text-[11px] text-zinc-500">Detectează prețuri corupte și le resetează la valoarea din eMAG.</p>
          <button data-testid="settings-heal-prices-btn" onClick={runHeal} disabled={loading === "heal"}
            className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-medium text-rose-200 hover:bg-rose-500/20 disabled:opacity-50">
            {loading === "heal" ? "Scanez..." : "Detectează & repară"}
          </button>
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
          <p className="text-sm font-semibold text-zinc-100">Migrează indexuri</p>
          <p className="mt-0.5 text-[11px] text-zinc-500">One-off: drop index legacy pe _emag_id (deja rulat).</p>
          <button data-testid="settings-migrate-btn" onClick={runMigrate} disabled={loading === "migrate"}
            className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-200 hover:bg-amber-500/20 disabled:opacity-50">
            {loading === "migrate" ? "Migrez..." : "Rulează migrare"}
          </button>
        </div>
      </div>
    </div>
  );
}



function PublicManualOrderSection() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/settings/public-manual-order`);
      setCfg(r.data || {});
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    if (cfg?.token && !window.confirm("Generezi un token NOU? Vechiul link nu va mai funcționa.")) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/settings/public-manual-order/regenerate`);
      setCfg({ ...(cfg || {}), ...r.data });
      toast.success("Token nou generat. Distribuie noul link angajaților.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggle = async (enabled) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/settings/public-manual-order/toggle`, { enabled });
      setCfg({ ...(cfg || {}), ...r.data });
      toast.success(enabled ? "Formular ACTIVAT" : "Formular DEZACTIVAT");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const baseUrl = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
  const fullLink = cfg?.token
    ? `${baseUrl}/manual-order-form#token=${cfg.token}`
    : "";

  const copyLink = async () => {
    if (!fullLink) return;
    try {
      await navigator.clipboard.writeText(fullLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copiat în clipboard");
    } catch (e) {
      toast.error("Nu am putut copia. Selectează manual textul.");
    }
  };

  return (
    <section
      data-testid="settings-public-manual-section"
      className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5"
    >
      <div className="mb-4 flex items-center gap-2">
        <Link2 className="h-4 w-4 text-emerald-400" />
        <h2 className="text-base font-semibold text-zinc-100">Link public Comandă manuală</h2>
      </div>
      <p className="mb-4 text-xs text-zinc-500">
        Linkul de mai jos îl distribui angajaților fără cont în aplicație. Ei pot crea
        comenzi telefonice direct din browser; comenzile apar în tab-ul Comenzi cu eticheta
        &bdquo;COMANDĂ MANUALĂ&rdquo; și primești notificare în clopoțel.
      </p>

      {!cfg?.token && (
        <div className="bg-amber-500/10 border border-amber-700/40 rounded-lg p-3 mb-4 text-sm text-amber-200">
          Niciun token generat încă. Apasă &bdquo;Generează link&rdquo; mai jos pentru a-l crea.
        </div>
      )}

      {cfg?.token && (
        <>
          <div className="mb-3">
            <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-2">
              Link complet (copiază-l și trimite-l angajaților)
            </label>
            <div className="flex items-center gap-2">
              <input
                data-testid="public-manual-link"
                readOnly
                value={fullLink}
                onClick={(e) => e.target.select()}
                className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs font-mono text-emerald-300 outline-none"
              />
              <button
                data-testid="public-manual-copy"
                onClick={copyLink}
                className="inline-flex items-center gap-1 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-200"
              >
                <Copy className="h-3.5 w-3.5" />
                {copied ? "Copiat!" : "Copiază"}
              </button>
            </div>
            <p className="mt-2 text-[11px] text-zinc-500">
              Status:{" "}
              {cfg.enabled ? (
                <span className="text-emerald-300 font-medium">ACTIV</span>
              ) : (
                <span className="text-rose-300 font-medium">DEZACTIVAT</span>
              )}
              {cfg.updated_at && (
                <> · ultima modificare {new Date(cfg.updated_at).toLocaleString("ro-RO")}</>
              )}
            </p>
          </div>
        </>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="public-manual-regenerate"
          onClick={regenerate}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          {cfg?.token ? "Generează token NOU" : "Generează link"}
        </button>
        {cfg?.token && (
          <button
            data-testid="public-manual-toggle"
            onClick={() => toggle(!cfg.enabled)}
            disabled={loading}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium disabled:opacity-50 ${
              cfg.enabled
                ? "border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20"
                : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
            }`}
          >
            <Power className="h-3.5 w-3.5" />
            {cfg.enabled ? "Dezactivează formular" : "Activează formular"}
          </button>
        )}
      </div>
    </section>
  );
}



// ─── Manual warranty issuer ────────────────────────────────────────────
// Operatorul introduce manual un numar de comanda (ex. vanzare offline sau
// factura pe hartie) si alege un produs din catalog. Backend genereaza PDF-ul
// prin acelasi generator ca la comenzile eMAG/Gomag; nu se salveaza in DB.
function ManualWarrantySection() {
  const [orderNumber, setOrderNumber] = useState("");
  const [lang, setLang] = useState("ro");
  const [query, setQuery] = useState("");
  const [queryDebounced, setQueryDebounced] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selected, setSelected] = useState(null); // {name, sku}
  const [generating, setGenerating] = useState(false);

  // Debounce the product search to limit API calls (350ms)
  useEffect(() => {
    const t = setTimeout(() => setQueryDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!queryDebounced) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/products`, {
          params: { search: queryDebounced, show_archived: false },
        });
        if (cancelled) return;
        const items = Array.isArray(r.data?.products) ? r.data.products
          : Array.isArray(r.data?.items) ? r.data.items
          : Array.isArray(r.data) ? r.data : [];
        // De-duplicate by part_number/id. The same SKU can exist on multiple
        // marketplaces (eMAG RO/HU/BG, Gomag) each with its own localized
        // name. For a Romanian-only warranty certificate we prefer the
        // Romanian variant. Hungarian/Bulgarian variants have distinctive
        // tokens (`töltő`, `nélküli`, `ben`, `зарядно`) so we score DOWN
        // for those; Romanian variants start with words like Incarcator /
        // Cablu / Husa / Suport.
        const roStarts = /^(incarcator|incarcare|cablu|husa|suport|statie|statia|adaptor|baterie)/i;
        const nonRoTokens = /(töltő|nélküli|egyidej|töltés|állomás|зарядно|безжично|беспроводн)/i;
        const score = (p) => {
          const n = (p.name || "").trim();
          let s = 0;
          if (roStarts.test(n)) s += 3;
          if (nonRoTokens.test(n)) s -= 5;
          // Prefer rows with English fallback (plain ASCII) over diacritic-heavy ones.
          if (/^[\x20-\x7E]+$/.test(n)) s += 1;
          return s;
        };
        const bestBySku = new Map();
        for (const p of items) {
          const key = p.part_number || p.id;
          if (!key) continue;
          const prev = bestBySku.get(key);
          if (!prev || score(p) > score(prev)) bestBySku.set(key, p);
        }
        setResults(Array.from(bestBySku.values()).slice(0, 12));
        setShowResults(true);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Eroare căutare produse");
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [queryDebounced]);

  const pickProduct = (p) => {
    setSelected({ name: p.name || "", sku: p.part_number || p.sku || "" });
    setQuery(p.name || "");
    setShowResults(false);
  };

  const clearProduct = () => {
    setSelected(null);
    setQuery("");
    setResults([]);
  };

  const generate = async () => {
    if (!selected?.name) { toast.error("Alege un produs din listă"); return; }
    if (!orderNumber.trim()) { toast.error("Introdu numărul comenzii"); return; }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/warranties/manual`, {
        product_name: selected.name,
        order_number: orderNumber.trim(),
        lang,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Garantie Comanda ${orderNumber.trim()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Garanție ${lang.toUpperCase()} generată pentru ${orderNumber.trim()}`);
    } catch (e) {
      // For blob responses, error detail is inside a Blob too — parse it.
      let msg = e.message || "Eroare la generare";
      try {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) msg = parsed.detail;
        } else if (e.response?.data?.detail) {
          msg = e.response.data.detail;
        }
      } catch (_e) { /* ignore parse error */ }
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section
      data-testid="manual-warranty-section"
      className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6"
    >
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-500/15 text-amber-300">
          <ShieldCheck className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-zinc-100">Emite garanție (manual)</h2>
          <p className="text-[11px] text-zinc-500">
            Pentru comenzi offline sau facturi pe hârtie — caută produsul după <strong>nume sau SKU</strong> (ex: <code className="rounded bg-zinc-800 px-1">BRAG-99</code>), adaugă numărul comenzii și alege limba.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Product picker */}
        <div className="relative">
          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            Produs
          </label>
          {selected ? (
            <div className="flex items-center justify-between rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-amber-100">{selected.name}</p>
                {selected.sku && <p className="truncate text-[10px] text-amber-300/70">SKU: {selected.sku}</p>}
              </div>
              <button
                data-testid="manual-warranty-clear-product"
                onClick={clearProduct}
                className="ml-2 rounded p-1 text-amber-300/70 transition hover:bg-amber-500/20 hover:text-amber-200"
                title="Schimbă produsul"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="manual-warranty-product-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => results.length > 0 && setShowResults(true)}
                placeholder="Caută după nume sau SKU..."
                className="w-full rounded-lg border border-zinc-800 bg-zinc-950 py-2 pl-9 pr-3 text-sm text-zinc-100 outline-none focus:border-amber-500/50"
              />
              {showResults && (queryDebounced.length > 0) && (
                <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl">
                  {searching && (
                    <div className="px-3 py-2 text-[11px] text-zinc-500">Se caută...</div>
                  )}
                  {!searching && results.length === 0 && (
                    <div className="px-3 py-3 text-center text-[11px] text-zinc-500">Niciun produs găsit</div>
                  )}
                  {!searching && results.map((p) => (
                    <button
                      key={p.id || p._id || p.part_number}
                      data-testid={`manual-warranty-result-${p.part_number || p.id}`}
                      onClick={() => pickProduct(p)}
                      className="flex w-full items-start gap-2 border-b border-zinc-900 px-3 py-2 text-left transition last:border-b-0 hover:bg-zinc-900/60"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs text-zinc-100">{p.name || "—"}</p>
                        <p className="truncate text-[10px] text-zinc-500">
                          {p.part_number ? `SKU: ${p.part_number}` : ""}
                          {p.brand ? ` · ${p.brand}` : ""}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Order number input */}
        <div>
          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            Nr. comandă
          </label>
          <input
            data-testid="manual-warranty-order-number"
            type="text"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder="ex. 12345 / OFFLINE-042"
            className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-amber-500/50"
          />
        </div>
      </div>

      {/* Language + generate */}
      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            Limbă certificat
          </label>
          <div className="flex gap-1.5">
            {[
              { k: "ro", label: "🇷🇴 Română" },
              { k: "en", label: "🇬🇧 English" },
            ].map((opt) => (
              <button
                key={opt.k}
                data-testid={`manual-warranty-lang-${opt.k}`}
                onClick={() => setLang(opt.k)}
                className={`rounded-full border px-4 py-1.5 text-[11px] font-medium transition ${
                  lang === opt.k
                    ? "border-amber-500/50 bg-amber-500/15 text-amber-200"
                    : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <button
          data-testid="manual-warranty-generate"
          onClick={generate}
          disabled={generating || !selected?.name || !orderNumber.trim()}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 text-sm font-semibold text-zinc-900 shadow-lg shadow-amber-500/20 transition hover:from-amber-400 hover:to-orange-400 disabled:opacity-40"
        >
          <FileDown className="h-4 w-4" />
          {generating ? "Se generează..." : "Generează garanție PDF"}
        </button>
      </div>
    </section>
  );
}



// ─── Sameday: Kg extra pe SKU + Cutoff formula v2 ─────────────────
function SamedayProductKgExtraSection() {
  const [items, setItems] = useState([]);
  const [cutoff, setCutoff] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ sku: "", kg_extra: "", name: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/settings/sameday/product-kg-extra`),
        axios.get(`${API}/settings/sameday/formula-v2-cutoff`),
      ]);
      setItems(r1.data?.items || []);
      setCutoff(r2.data?.cutoff || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut încărca tabelul");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveCutoff = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/sameday/formula-v2-cutoff`, { cutoff });
      toast.success("Cutoff salvat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvarea a eșuat");
    } finally {
      setSaving(false);
    }
  };

  const addItem = async () => {
    const sku = (draft.sku || "").trim().toUpperCase();
    const kg = Number(draft.kg_extra);
    if (!sku) { toast.error("SKU obligatoriu"); return; }
    if (!kg || kg <= 0) { toast.error("Kg extra trebuie > 0"); return; }
    try {
      await axios.post(`${API}/settings/sameday/product-kg-extra`, { sku, kg_extra: kg, name: draft.name || "" });
      setDraft({ sku: "", kg_extra: "", name: "" });
      toast.success(`SKU ${sku} adaugat`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvarea a eșuat");
    }
  };

  const removeItem = async (sku) => {
    if (!window.confirm(`Stergi SKU ${sku} din tabel?`)) return;
    try {
      await axios.delete(`${API}/settings/sameday/product-kg-extra/${encodeURIComponent(sku)}`);
      toast.success(`SKU ${sku} sters`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stergerea a esuat");
    }
  };

  return (
    <section className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="settings-sameday-kg-extra">
      <div className="mb-4 flex items-center gap-2">
        <SettingsIcon className="h-4 w-4 text-zinc-400" />
        <h2 className="text-base font-semibold text-zinc-100">Sameday · Kg extra pe produs</h2>
      </div>
      <p className="mb-4 text-xs text-zinc-500">
        Produse voluminoase care adauga kg suplimentari la calculul costului AWB.
        Se aplica per unitate (× cantitate). Ex: BRAG-153 espressor = 1 kg &rarr; 2 espressoare = 2 kg extra.
      </p>

      {/* Cutoff data */}
      <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
        <label className="mb-1 block text-[10px] uppercase tracking-wider text-amber-300">
          Data cutoff formula v2 (fuel pe base+extra + notificare 0,15 + kg SKU)
        </label>
        <p className="mb-2 text-[10px] text-zinc-500">
          AWB-urile emise <b>&ge;</b> aceasta data folosesc formula noua. Cele mai vechi raman pe formula veche (paritate cu istoric).
          Lasa gol pentru a dezactiva formula v2.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            data-testid="settings-sameday-v2-cutoff-input"
            value={cutoff}
            onChange={(e) => setCutoff(e.target.value)}
            className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-amber-500/50 focus:outline-none"
          />
          <button
            data-testid="settings-sameday-v2-cutoff-save"
            onClick={saveCutoff}
            disabled={saving}
            className="rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-zinc-900 hover:bg-amber-400 disabled:opacity-50"
          >
            {saving ? "..." : "Salveaza cutoff"}
          </button>
          {!cutoff && <span className="text-[11px] text-zinc-500">— formula v2 dezactivata</span>}
        </div>
      </div>

      {/* Formular adaugare */}
      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_120px_1fr_auto]">
        <input
          data-testid="settings-kg-extra-sku"
          placeholder="SKU (ex: BRAG-153)"
          value={draft.sku}
          onChange={(e) => setDraft((d) => ({ ...d, sku: e.target.value }))}
          className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
        />
        <input
          data-testid="settings-kg-extra-kg"
          type="number"
          step="0.1"
          min="0.1"
          placeholder="kg extra"
          value={draft.kg_extra}
          onChange={(e) => setDraft((d) => ({ ...d, kg_extra: e.target.value }))}
          className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
        />
        <input
          data-testid="settings-kg-extra-name"
          placeholder="Denumire (optional)"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
        />
        <button
          data-testid="settings-kg-extra-add"
          onClick={addItem}
          className="inline-flex items-center gap-1 rounded-lg bg-orange-500 px-3 py-2 text-xs font-semibold text-white hover:bg-orange-400"
        >
          + Adauga
        </button>
      </div>

      {/* Tabel */}
      {loading ? (
        <div className="p-3 text-xs text-zinc-500">Se incarca…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-800 p-4 text-center text-xs text-zinc-500">
          Niciun produs configurat. Adauga unul mai sus.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="w-full text-xs">
            <thead className="bg-zinc-950/60">
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="px-3 py-2">SKU</th>
                <th className="px-3 py-2 text-right">Kg extra / buc</th>
                <th className="px-3 py-2">Denumire</th>
                <th className="px-3 py-2 w-16" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.sku} data-testid={`settings-kg-extra-row-${it.sku}`} className="border-t border-zinc-800/60 hover:bg-zinc-800/30">
                  <td className="px-3 py-2 font-mono text-orange-300">{it.sku}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-100">{Number(it.kg_extra).toFixed(2)} kg</td>
                  <td className="px-3 py-2 text-zinc-400">{it.name || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      data-testid={`settings-kg-extra-delete-${it.sku}`}
                      onClick={() => removeItem(it.sku)}
                      className="text-[11px] text-rose-400 hover:text-rose-300"
                    >
                      Sterge
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
